import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  ArrowUpRight,
  CreditCard,
  Globe,
  ShieldCheck,
} from 'lucide-react';

type Currency = 'USD' | 'EUR';

interface CourseItem {
  name: string;
  price: number;
}

const PAYMENT_LINKS: Record<Currency, { amount: number; url: string }[]> = {
  USD: [
    { amount: 3000, url: 'https://monecle.com/buy/97456' },
    { amount: 2500, url: 'https://monecle.com/buy/99901' },
    { amount: 2000, url: 'https://monecle.com/buy/99897' },
    { amount: 1500, url: 'https://monecle.com/buy/99899' },
    { amount: 1000, url: 'https://monecle.com/buy/97078' },
    { amount: 800, url: 'https://monecle.com/buy/97077' },
    { amount: 700, url: 'https://monecle.com/buy/96302' },
    { amount: 500, url: 'https://monecle.com/buy/96301' },
    { amount: 300, url: 'https://monecle.com/buy/97076' },
    { amount: 200, url: 'https://monecle.com/buy/96527' },
    { amount: 100, url: 'https://monecle.com/buy/96526' },
  ],
  EUR: [
    { amount: 3000, url: 'https://monecle.com/buy/99194' },
    { amount: 2500, url: 'https://monecle.com/buy/99902' },
    { amount: 2000, url: 'https://monecle.com/buy/99896' },
    { amount: 1500, url: 'https://monecle.com/buy/99900' },
    { amount: 1000, url: 'https://monecle.com/buy/99195' },
    { amount: 800, url: 'https://monecle.com/buy/99196' },
    { amount: 700, url: 'https://monecle.com/buy/99201' },
    { amount: 500, url: 'https://monecle.com/buy/99202' },
    { amount: 300, url: 'https://monecle.com/buy/99198' },
    { amount: 200, url: 'https://monecle.com/buy/99199' },
    { amount: 100, url: 'https://monecle.com/buy/99200' },
  ],
};

const BuyMonecle = () => {
  const { t, i18n } = useTranslation();
  const [currency, setCurrency] = useState<Currency>('USD');

  const courses = t('courses.items', { returnObjects: true }) as CourseItem[];
  const plans = PAYMENT_LINKS[currency];
  const symbol = currency === 'USD' ? '$' : '€';

  const courseByPrice = useMemo(() => {
    const map = new Map<number, string>();
    courses.forEach((course) => {
      map.set(course.price, course.name);
    });
    return map;
  }, [courses]);

  return (
    <div className="min-h-screen bg-crypto-black">
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6 }}
        className="fixed top-0 left-0 right-0 z-50 bg-crypto-black/90 backdrop-blur-xl border-b border-crypto-red/20"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-crypto-red to-crypto-gradient-end flex items-center justify-center">
                <span className="text-white font-bold text-xl">R</span>
              </div>
              <div className="hidden sm:block">
                <span className="text-xl font-bold text-white">RedLevel</span>
                <span className="text-crypto-red font-bold text-xl">Crypto</span>
              </div>
            </Link>

            <div className="flex items-center gap-3">
              <button
                onClick={() => i18n.changeLanguage(i18n.language === 'ru' ? 'en' : 'ru')}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-crypto-card hover:bg-crypto-card/80 transition-colors"
              >
                <Globe className="w-4 h-4 text-crypto-red" />
                <span className="text-sm font-medium">{i18n.language === 'ru' ? 'RU' : 'EN'}</span>
              </button>
              <Link
                to="/"
                className="flex items-center gap-2 text-gray-400 hover:text-crypto-red transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                <span className="hidden sm:inline">{t('buyMonecle.backHome')}</span>
              </Link>
            </div>
          </div>
        </div>
      </motion.header>

      <section className="pt-32 pb-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-crypto-red/10 via-transparent to-crypto-purple/10" />
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-crypto-red/5 rounded-full blur-[150px]" />

        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <span className="inline-block px-3 py-1 rounded-full bg-crypto-red/10 border border-crypto-red/30 text-crypto-red text-xs font-semibold tracking-wider uppercase mb-4">
              {t('buyMonecle.badge')}
            </span>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">
              {t('buyMonecle.title')}
            </h1>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              {t('buyMonecle.subtitle')}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="flex flex-col items-center gap-4 mb-12"
          >
            <p className="text-sm text-gray-400">{t('buyMonecle.currency')}</p>
            <div className="inline-flex p-1 bg-crypto-card border border-crypto-red/20 rounded-2xl">
              {(['USD', 'EUR'] as Currency[]).map((code) => (
                <button
                  key={code}
                  onClick={() => setCurrency(code)}
                  className={`px-8 py-3 rounded-xl font-bold transition-all ${
                    currency === code
                      ? 'bg-crypto-red text-white shadow-lg shadow-crypto-red/20'
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  {code === 'USD' ? t('buyMonecle.usd') : t('buyMonecle.eur')}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-400">
              <ShieldCheck className="w-4 h-4 text-crypto-red" />
              {t('buyMonecle.secure')}
            </div>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {plans.map((plan, index) => {
              const name = courseByPrice.get(plan.amount) || t('buyMonecle.fallbackName');
              const isVip = plan.amount === 3000;

              return (
                <motion.a
                  key={`${currency}-${plan.amount}`}
                  href={plan.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.05 * index }}
                  className={`group relative rounded-3xl p-6 overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl hover:shadow-crypto-red/20 ${
                    isVip
                      ? 'bg-crypto-card border-2 border-crypto-red'
                      : 'bg-crypto-card border border-crypto-red/15 hover:border-crypto-red/40'
                  }`}
                >
                  {isVip && (
                    <div className="absolute top-0 right-0 px-3 py-1 bg-crypto-red text-[10px] font-bold uppercase tracking-wider rounded-bl-xl">
                      VIP
                    </div>
                  )}
                  <div className="h-1.5 w-12 rounded-full bg-crypto-red mb-6" />
                  <h3 className="text-white font-bold text-lg mb-2 pr-6">{name}</h3>
                  <div className="flex items-baseline gap-1 mb-6">
                    <span className="text-4xl font-bold text-white">
                      {symbol}
                      {plan.amount}
                    </span>
                    <span className="text-gray-500 text-sm">{currency}</span>
                  </div>
                  <div className="flex items-center justify-between w-full px-4 py-3 rounded-xl bg-crypto-red text-white font-bold group-hover:bg-crypto-redDark transition-colors">
                    <span className="flex items-center gap-2">
                      <CreditCard className="w-4 h-4" />
                      {t('buyMonecle.pay')}
                    </span>
                    <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </div>
                </motion.a>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default BuyMonecle;
